/**
 * Table row for an establishment.
 */
import * as React from 'react';
import { Building2, MapPin, DoorOpen, Clock } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import {
  ESTABLISHMENT_STATUS_CONFIG,
  PROSPECTION_STATUS_CONFIG,
  ESTABLISHMENT_CATEGORY_CONFIG,
  ESTABLISHMENT_PRIORITY_NETWORK_CONFIG,
  ESTABLISHMENT_TYPE_CONFIG,
} from './establishment-configs';
import {
  computePartnershipSeniority,
  formatEstablishmentCoordinatorsLabel,
  formatEstablishmentDepartment,
  formatEstablishmentOpeningDelay,
  formatQpvLabel,
  getEstablishmentTrackingDisplay,
} from '@/components/establishments/establishment-table-utils';
import type { Establishment } from '@/types/establishment.types';

/** Props for {@link EstablishmentRow}. */
interface EstablishmentRowProps {
  establishment: Establishment;
  onView?: (id: string) => void;
  onEdit?: (establishment: Establishment) => void;
  onDelete?: (id: string) => void;
  pageActions?: {
    canEditRow?: boolean;
    canDeleteRow?: boolean;
  };
}

/** List-mode row; opening delay is only shown when prospection is validated. */
export const EstablishmentRow: React.FC<EstablishmentRowProps> = ({
  establishment,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const canEdit = pageActions?.canEditRow ?? true;
  const canDelete = pageActions?.canDeleteRow ?? true;
  const typeConfig =
    ESTABLISHMENT_TYPE_CONFIG[establishment.type as string] || ESTABLISHMENT_TYPE_CONFIG.college;
  const statusConfig =
    ESTABLISHMENT_STATUS_CONFIG[establishment.status] || ESTABLISHMENT_STATUS_CONFIG.active;
  const prospectionKey = establishment.prospectionStatus ?? 'to_define';
  const prospectionConfig =
    PROSPECTION_STATUS_CONFIG[prospectionKey] || PROSPECTION_STATUS_CONFIG.to_define;
  const TypeIcon = typeConfig.icon;
  const StatusIcon = statusConfig.icon;
  const ProspectionIcon = prospectionConfig.icon;
  const coordinatorsLabel = formatEstablishmentCoordinatorsLabel(establishment.coordinators);
  const openingDelayLabel = formatEstablishmentOpeningDelay(
    prospectionKey,
    establishment.openingDelayDays,
  );
  const tracking = getEstablishmentTrackingDisplay(establishment);
  const classroomsCount = establishment.roomsCount ?? 0;

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Building2 className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="font-medium text-foreground">{establishment.name}</span>
        </div>
      </td>

      <td>
        <LabelBadge
          label={typeConfig.label}
          icon={TypeIcon}
          variant={typeConfig.badgeVariant}
          color={typeConfig.badgeColor}
          size="md"
        />
      </td>

      <td>
        {establishment.establiCategory &&
        ESTABLISHMENT_CATEGORY_CONFIG[establishment.establiCategory] ? (
          <LabelBadge
            label={ESTABLISHMENT_CATEGORY_CONFIG[establishment.establiCategory].label}
            icon={ESTABLISHMENT_CATEGORY_CONFIG[establishment.establiCategory].icon}
            variant="outlined"
            color={ESTABLISHMENT_CATEGORY_CONFIG[establishment.establiCategory].badgeColor}
            size="md"
          />
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </td>

      <td>
        {establishment.priorityNetwork &&
        ESTABLISHMENT_PRIORITY_NETWORK_CONFIG[establishment.priorityNetwork] ? (
          <LabelBadge
            label={ESTABLISHMENT_PRIORITY_NETWORK_CONFIG[establishment.priorityNetwork].label}
            icon={ESTABLISHMENT_PRIORITY_NETWORK_CONFIG[establishment.priorityNetwork].icon}
            variant="outlined"
            color={ESTABLISHMENT_PRIORITY_NETWORK_CONFIG[establishment.priorityNetwork].badgeColor}
            size="md"
          />
        ) : (
          <span className="text-muted-foreground text-sm">—</span>
        )}
      </td>

      <td>
        <span className="text-sm text-foreground">{formatQpvLabel(establishment.isQpv)}</span>
      </td>

      <td>
        <span className="text-sm text-foreground">
          {establishment.ipsValue != null ? (
            <span title={establishment.ipsYear ? `Année ${establishment.ipsYear}` : undefined}>
              {establishment.ipsValue}
            </span>
          ) : (
            '—'
          )}
        </span>
      </td>

      <td>
        <span className="text-sm text-foreground">{establishment.city?.trim() || '—'}</span>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="text-foreground">
            {formatEstablishmentDepartment(
              establishment.department,
              establishment.departmentCode,
            ) || 'Non défini'}
          </span>
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          <span className="text-foreground">
            {computePartnershipSeniority(establishment.engagementStartDate)}
          </span>
        </div>
      </td>

      <td className="text-center">
        <div className="flex items-center gap-2 justify-center">
          <DoorOpen className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          <span className="text-foreground">{classroomsCount}</span>
        </div>
      </td>

      <td className="text-center">
        <span className="text-foreground text-sm tabular-nums">{tracking.students}</span>
      </td>

      <td className="text-center">
        <span className="text-foreground text-sm tabular-nums">{tracking.tutors}</span>
      </td>

      <td className="text-center">
        <span className="text-foreground text-sm tabular-nums">{tracking.vsc}</span>
      </td>

      <td>
        <LabelBadge
          label={prospectionConfig.label}
          icon={ProspectionIcon}
          variant={prospectionConfig.badgeVariant}
          color={prospectionConfig.badgeColor}
          size="md"
        />
      </td>

      <td>
        <span className="text-foreground">{coordinatorsLabel}</span>
      </td>

      <td>
        <span className="text-foreground">{openingDelayLabel}</span>
      </td>

      <td className="text-center">
        <span className="text-foreground">{establishment.prospectMeetingsCount ?? 0}</span>
      </td>

      <td>
        <LabelBadge
          label={statusConfig.label}
          icon={StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="md"
        />
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView?.(establishment.id)} />
          {canEdit && <Button preset="row-edit" onClick={() => onEdit?.(establishment)} />}
          {canDelete && <Button preset="row-delete" onClick={() => onDelete?.(establishment.id)} />}
        </div>
      </td>
    </TableRow>
  );
};
